import styles from './prosentvis-overgang.module.css'
import { debounce } from 'debounce'

// eslint-disable-next-line import/default
import { CanvasManager } from './overgang/canvas-manager.js'

export default class ProsentvisOvergang extends HTMLElement {
  static get observedAttributes() {
    return ['data-progress']
  }

  connectedCallback() {
    this.appendChild(document.createElement('canvas'))
    /*
    Disabling workers as webpack 5 does not support inline workers:
    https://github.com/webpack/webpack/discussions/14066
    */
    /*
    if ('OffscreenCanvas' in window) {
      this._worker = new Worker(
        new URL('./overgang/prosentvis-overgang.worker.js', import.meta.url)
      )
      const offscreen = this.canvas.transferControlToOffscreen()
      this._worker.postMessage({ canvas: offscreen }, [offscreen])
    }
     */

    this._debouncedUpdateCanvasWidth = debounce(this._updateCanvasWidth, 300)
    this._manager = new CanvasManager(this.canvas)

    window.addEventListener('resize', this._debouncedUpdateCanvasWidth)
    this._debouncedUpdateCanvasWidth()

    setTimeout(() => {
      this.querySelectorAll('img').forEach((img) => {
        img.addEventListener('load', this._debouncedUpdateCanvasWidth)
      })
    })
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._debouncedUpdateCanvasWidth)
  }

  attributeChangedCallback(name, prevValue, newValue) {
    const valueChanged = prevValue !== newValue
    if (!valueChanged) {
      return
    }

    if (name === 'data-progress') {
      this._prosentChanged()
    }
  }

  _prosentChanged() {
    this._updateMask()
  }

  _updateCanvasWidth = async () => {
    const canvas = this.canvas
    const images = this.images
    if (
      images.length < 2 ||
      images.some((image) => !image.complete) ||
      canvas.offsetWidth === this._currentCanvasWidth ||
      this.image.clientHeight === 0 ||
      this.mask.clientHeight === 0 ||
      this.image.naturalHeight === 0 ||
      this.mask.naturalHeight === 0
    ) {
      return
    }
    this._currentCanvasWidth = canvas.offsetWidth
    /* We're loaded */
    const dpr = Math.min(window.devicePixelRatio, 2)
    if (this._worker) {
      const image = await createImageBitmap(this.image)
      const mask = await createImageBitmap(this.mask)
      this._worker.postMessage(
        {
          image,
          mask,
          canvasWidth: canvas.offsetWidth,
          canvasHeight: canvas.offsetHeight,
          dpr,
        },
        [image, mask],
      )
    } else {
      this._manager.updateDpr(canvas.offsetWidth, canvas.offsetHeight, dpr)
      this._manager.updateImages(this.image, this.mask)
    }
    this._ready = true
    this._updateMask()
  }

  _updateMask = () => {
    if (!this._ready) {
      return
    }

    if (this._worker) {
      this._worker.postMessage({ progress: this.progress, range: this.range })
      if (this.canvas.style.visibility !== 'visible') {
        /* Hide image and show canvas */
        this.canvas.style.visibility = 'visible'
        this.image.style.visibility = 'hidden'
      }
    } else {
      if (this._animationFrame) {
        return // Already painting
      }
      this._animationFrame = requestAnimationFrame(() => {
        const progress = this.progress
        const range = this.range
        this._manager.updateMask(progress, range)

        delete this._animationFrame
        if (this.canvas.style.visibility !== 'visible') {
          /* Hide image and show canvas */
          this.canvas.style.visibility = 'visible'
          this.image.style.visibility = 'hidden'
        }
      })
    }
  }

  get image() {
    return this.querySelector(`.${styles.image}`)
  }

  get mask() {
    return this.querySelector(`.${styles.mask}`)
  }

  get images() {
    /* eslint-disable compat/compat */
    return Array.from(this.querySelectorAll('img'))
  }

  get canvas() {
    return this.querySelector('canvas')
  }

  get progress() {
    return parseFloat(this.getAttribute('data-progress') || '0.0')
  }

  /* Range between black and white point */
  get range() {
    return parseFloat(this.getAttribute('range') || '1.0')
  }

  set progress(val) {
    this.setAttribute('data-progress', val.toFixed(4))
  }
}
